import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import { styles as tableStyles } from "../viz-table/viz-table.styles";
import { styles as spotlightStyles } from "../../utils/spotlight";
import { fromPx, toPx } from "../../utils/units";

import type { SlideViewMode } from "../slide/slide";
import type { SlideState, TableVisualData } from "../../types.d";

@customElement("bp-table-slide")
export class TableSlide extends LitElement {
  static styles = [
    tableStyles,
    spotlightStyles,
    css`
      .table {
        width: var(--table-width);
      }

      table {
        transition:
          color var(--spotlight-transition-duration),
          background-color var(--spotlight-transition-duration),
          border var(--spotlight-transition-duration),
          box-shadow var(--spotlight-transition-duration);
      }

      a {
        transition: color var(--spotlight-transition-duration);
      }

      table th {
        transition:
          color var(--spotlight-transition-duration),
          background-color var(--spotlight-transition-duration),
          box-shadow var(--spotlight-transition-duration);
      }

      table td.spotlighted {
        border-width: 2.01px;
      }

      table td {
        transition:
          color var(--spotlight-transition-duration),
          border-color var(--spotlight-transition-duration),
          border-width 0s;
      }

      table tfoot {
        transition: color var(--spotlight-transition-duration);
      }
    `,
  ];

  @property({ attribute: "view-mode" })
  viewMode?: SlideViewMode = "default";

  @property()
  slideState?: SlideState;

  @property({ type: Boolean, attribute: "hide-title" })
  hideTitle: boolean = false;

  @property({ type: Boolean, attribute: "title-wraps-2-lines" })
  titleWraps2Lines: boolean = false;

  @property()
  data?: TableVisualData;

  render() {
    if (!this.data) return;

    if (this.data.minWidth) {
      const minWidthVariable = toPx(
        Math.min(fromPx(this.data.minWidth), fromPx(this.data.slideWidth)),
      );
      if (minWidthVariable) {
        this.style.setProperty("--min-width", minWidthVariable);
      }
    }

    if (this.data.slideWidth) {
      this.style.setProperty("--table-width", this.data.slideWidth);
    }
    return html`
      <bp-zoomable-slide
        ?hide-title=${this.hideTitle}
        ?title-wraps-2-lines=${this.titleWraps2Lines}
        view-mode="${this.viewMode}"
        .slideState=${this.slideState}
      >
        <div slot="title">
          <slot name="title"></slot>
        </div>
        <div slot="content" class="table">${unsafeHTML(this.data.body)}</div>
      </bp-zoomable-slide>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-table-slide": TableSlide;
  }
}
